<template>
  <v-container
    v-if="materials"
    class="bordered bg-white">
    <v-row>
      <v-col>
        <DataTable
          :items="materials"
          :headers="headers"
          :summary="summary" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { useSpaceStore } from "@/stores/spaceStore";

import DataTable from "@/components/Elements/DataTable.vue";

const spaceStore = useSpaceStore();

const { t } = useTranslation();
const variant = ref(null);

const props = defineProps({
  nodeId: {
    type: String,
    required: true
  }
});

const materials = computed(() => {
  return Object.entries(spaceStore.calculationElements).reduce(
    (result, [id, element]) => {
      if (
        id.endsWith("material") &&
        spaceStore.calculationElements[props.nodeId].children.includes(id)
      ) {
        result.push(
          Object.assign({}, element, {
            id,
            material_type: t(`entities.${id.split("_")[1]}.name`),
            material_price_material:
              element.material_price_material +
              " (" +
              t("units.e_per_unit_long", {
                unit: t(
                  `entities.material.selects.unit_material.${element.unit_material}`
                )
              }) +
              ")",
            quantity_of_material_material:
              element.quantity_of_material_material +
              " (" +
              t("units.unit_per_piece_long", {
                unit: t(
                  `entities.material.selects.unit_material.${element.unit_material}`
                )
              }) +
              ")"
          })
        );
      }
      return result;
    },
    []
  );
});

const summary = computed(() => {
  return {
    direct_material_costs_material: materials.value.reduce(
      (sum, item) => sum + Number(item.direct_material_costs_material),
      0
    ),
    material_overhead_costs_material: materials.value.reduce(
      (sum, item) => sum + Number(item.material_overhead_costs_material),
      0
    ),
    interest_costs_on_material_stock_material: materials.value.reduce(
      (sum, item) =>
        sum + Number(item.interest_costs_on_material_stock_material),
      0
    ),
    material_scrap_costs_material: materials.value.reduce(
      (sum, item) => sum + Number(item.material_scrap_costs_material),
      0
    )
  };
});

onMounted(() => {
  variant.value = spaceStore.calculationElements[props.nodeId];
});

const headers = computed(() => [
  {
    title: t("entities.variant.fields.material_name"),
    align: "start",
    sortable: false,
    key: "name"
  },
  {
    title: t("entities.variant.fields.material_type"),
    key: "material_type",
    value: (item) => item.material_type ?? "Einkauf",
    sortable: false
  },
  {
    title: t("entities.variant.fields.sourcing_type"),
    key: "sourcing_type",
    sortable: false
  },
  {
    title: t("entities.variant.fields.material_price_material"),
    key: "material_price_material",
    sortable: true
  },
  {
    title: t("entities.variant.fields.quantity_of_material_material"),
    key: "quantity_of_material_material",
    sortable: true
  },
  {
    title: t("entities.variant.fields.direct_material_costs_material"),
    key: "direct_material_costs_material",
    sortable: true
  },
  {
    title: t("entities.variant.fields.material_overhead_costs_material"),
    key: "material_overhead_costs_material",
    sortable: true
  },
  {
    title: t(
      "entities.variant.fields.interest_costs_on_material_stock_material"
    ),
    key: "interest_costs_on_material_stock_material",
    value: (item) => item.interest_costs_on_material_stock_material ?? 0,
    sortable: true
  },
  {
    title: t("entities.variant.fields.material_scrap_costs_material"),
    key: "material_scrap_costs_material",
    sortable: true
  }
]);
</script>

<style scoped>
:deep() .v-data-table-header__content {
  font-weight: 700;
  font-size: smaller;
}

:deep() .v-table .v-table__wrapper > table > thead > tr > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

:deep() .v-table .v-table__wrapper > table > tbody > tr > td:not(:last-child),
.v-table .v-table__wrapper > table > tbody > tr > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
